import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RolePermissionDto } from '../../web-api-client';
import { useTranslation } from 'react-i18next';

interface RoleFormDialogProps {
  open: boolean;
  title: string;
  permissions: RolePermissionDto[];
  initialValues?: RoleFormValues;
  isSubmitting?: boolean;
  isLoading?: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (values: RoleFormValues) => void;
}

export interface RoleFormValues {
  name: string;
  description?: string;
  permissionIds: string[];
}

interface RoleFormErrors {
  name?: string;
  permissionIds?: string;
}

const defaultValues: RoleFormValues = {
  name: '',
  description: '',
  permissionIds: [],
};

const RoleFormDialog: React.FC<RoleFormDialogProps> = ({
  open,
  title,
  permissions,
  initialValues,
  isSubmitting = false,
  isLoading = false,
  errorMessage,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<RoleFormValues>(defaultValues);
  const [errors, setErrors] = useState<RoleFormErrors>({});

  useEffect(() => {
    if (open) {
      setValues({
        name: initialValues?.name ?? '',
        description: initialValues?.description ?? '',
        permissionIds: initialValues?.permissionIds ?? [],
      });
      setErrors({});
    }
  }, [open, initialValues]);

  const permissionOptions = useMemo(() => permissions, [permissions]);

  const handleSubmit = () => {
    const validationErrors: RoleFormErrors = {};

    if (!values.name.trim()) {
      validationErrors.name = t('roles.validation.nameRequired');
    }

    if (values.permissionIds.length === 0) {
      validationErrors.permissionIds = t('roles.validation.permissionsRequired');
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      onSubmit({
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        permissionIds: values.permissionIds,
      });
    }
  };

  const selectedPermissions = permissionOptions.filter((permission) => {
    const permissionId = permission.id ?? '';
    return values.permissionIds.includes(permissionId);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {isLoading ? (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="textSM" color="text.secondary">
              {t('roles.loadingSelection')}
            </Typography>
          </Box>
        ) : (
          <>
            <TextField
              label={t('roles.fields.name')}
              value={values.name}
              onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
              error={Boolean(errors.name)}
              helperText={errors.name}
              disabled={isSubmitting}
              autoFocus
            />

            <TextField
              label={t('roles.fields.description')}
              value={values.description}
              onChange={(event) => setValues((prev) => ({ ...prev, description: event.target.value }))}
              multiline
              minRows={3}
              disabled={isSubmitting}
            />

            <Box>
              <Typography variant="textSM" sx={{ fontWeight: 600, mb: 1 }}>
                {t('roles.fields.permissions')}
              </Typography>
              <Autocomplete
                multiple
                options={permissionOptions}
                disableCloseOnSelect
                value={selectedPermissions}
                onChange={(_, newValue) =>
                  setValues((prev) => ({
                    ...prev,
                    permissionIds: newValue
                      .map((option) => option.id)
                      .filter((id): id is string => typeof id === 'string'),
                  }))
                }
                getOptionLabel={(option) => option.action ?? ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('roles.fields.permissionsPlaceholder')}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Checkbox checked={selected} sx={{ ml: -1 }} />
                      <Box>
                        <Typography variant="textMD" sx={{ fontWeight: 600, display: 'block' }}>
                          {option.action}
                        </Typography>
                        {option.description && (
                          <Typography
                            variant="textXS"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                          >
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </li>
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.action}
                      size="small"
                    />
                  ))
                }
                disabled={isSubmitting}
                fullWidth
              />
              {errors.permissionIds && (
                <FormHelperText error>{errors.permissionIds}</FormHelperText>
              )}
            </Box>

            {errorMessage && (
              <FormHelperText error sx={{ fontSize: '0.9rem' }}>
                {errorMessage}
              </FormHelperText>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ gap: 1.5 }}>
        <Button
          variant="secondary"
          size="lg"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          size="lg"
          onClick={handleSubmit}
          disabled={isSubmitting || isLoading}
          sx={{ minWidth: 120 }}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleFormDialog;
